import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Stack,
  Typography,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Close, ReceiptLongOutlined, ZoomInRounded } from "@mui/icons-material";
import { getBranchExpenseDetail, evidenceUrl } from "api/financial";
import { getMyBranchExpenseDetail, branchCashEvidenceUrl } from "api/branchCash";
import AuthenticatedImage from "components/AuthenticatedImage";
import { MONO, money, fmtDate, SlipLightbox } from "./receiptParts";
import TransactionDetailDialog from "./TransactionDetailDialog";

function MetaRow({ label, value, valueColor }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.5 }}>
      <Typography sx={{ fontSize: 12.5 }} color="text.secondary">{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, textAlign: "right" }} color={valueColor || "text.primary"}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

// Detail for one BranchCashExpense (a staff-recorded branch cash expense, e.g.
// buying drinking water) — reason, receipt photo, requester, and either its
// approve/reject actions (when rendered with onApprove/onReject, i.e. from the
// Approvals screen) or its settled/rejected history.
// `mode="financial"` (default) is the manager drill-down (any station).
// `mode="branch"` is the station-scoped Branch Cash page (own stations only).
export default function ExpenseDetailDialog({
  open,
  expenseId,
  onClose,
  onApprove,
  onReject,
  busy = false,
  mode = "financial",
}) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [txId, setTxId] = useState(null);
  const fetchDetail = mode === "branch" ? getMyBranchExpenseDetail : getBranchExpenseDetail;
  const getEvidenceUrl = mode === "branch" ? branchCashEvidenceUrl : evidenceUrl;

  useEffect(() => {
    if (!open || !expenseId) return;
    setLoading(true);
    setD(null);
    fetchDetail(expenseId)
      .then((res) => setD(res && res.success ? res.data : null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, expenseId, mode]);

  const exp = d?.expense;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{t("branchCash.expenseDetail")}</Typography>
            {exp && (
              <Typography sx={{ fontSize: 12 }} color="text.secondary">{exp.stationId}</Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress color="secondary" /></Box>
        )}

        {!loading && exp && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "auto 1fr" },
                gap: 2,
                p: 2,
                borderRadius: "14px",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase" }} color="text.secondary">
                  {t("branchCash.amount")}
                </Typography>
                <Typography sx={{ fontFamily: MONO, fontWeight: 800, fontSize: 30, lineHeight: 1.1 }} color="error.main">
                  −{money(exp.amount, exp.currency)}
                </Typography>
                {exp.status === "pending_approval" && (
                  <Chip size="small" color="warning" sx={{ mt: 0.5 }} label={t("financial.pending")} />
                )}
              </Box>
              <Box>
                <MetaRow label={t("branchCash.expenseReason")} value={exp.reason} />
                <MetaRow label={t("branchCash.requestedBy")} value={exp.requestedBy?.name} />
                <MetaRow label={t("financial.date")} value={fmtDate(exp.createdAt)} />
              </Box>
            </Box>

            <Stack spacing={1} sx={{ mt: 2.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ReceiptLongOutlined fontSize="small" color="secondary" />
                <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{t("branchCash.receipt")}</Typography>
              </Stack>
              {exp.receiptPhoto?.filename ? (
                <Box
                  onClick={() => setLightbox(exp.receiptPhoto.filename)}
                  sx={{ position: "relative", cursor: "zoom-in", border: "1px solid", borderColor: "divider", borderRadius: "12px", overflow: "hidden", "&:hover .slipHint": { opacity: 1 } }}
                >
                  <AuthenticatedImage
                    src={getEvidenceUrl(exp.receiptPhoto.filename)}
                    alt={t("branchCash.receipt")}
                    style={{ width: "100%", height: "auto", maxHeight: "40vh", objectFit: "contain", display: "block" }}
                  />
                  <Box className="slipHint" sx={{ position: "absolute", bottom: 10, right: 10, display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.5, borderRadius: "8px", background: "rgba(20,18,30,.72)", color: "#fff", fontSize: 11, fontWeight: 600, opacity: 0.85, transition: "opacity .15s", pointerEvents: "none" }}>
                    <ZoomInRounded sx={{ fontSize: 15 }} />{t("financial.enlarge")}
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ fontSize: 12.5 }} color="text.secondary">{t("financial.noSlip")}</Typography>
              )}
            </Stack>

            {exp.status !== "pending_approval" && (
              <Box
                sx={{
                  mt: 2, p: 1.5, borderRadius: "12px", border: "1px solid",
                  borderColor: exp.status === "completed" ? "success.main" : "error.main",
                  bgcolor: exp.status === "completed" ? "rgba(46,125,50,0.06)" : "rgba(211,47,47,0.06)",
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 13 }} color={exp.status === "completed" ? "success.main" : "error.main"}>
                  {t(`financial.${exp.status === "completed" ? "completed" : "rejected"}`)}
                </Typography>
                {exp.status === "completed" && exp.approvedBy?.name && (
                  <Typography sx={{ fontSize: 12.5, mt: 0.5 }} color="text.secondary">
                    {t("financial.approvedBy")}: {exp.approvedBy.name}{exp.approvedBy.at ? ` · ${fmtDate(exp.approvedBy.at)}` : ""}
                  </Typography>
                )}
                {exp.status === "rejected" && exp.rejectionReason && (
                  <Typography sx={{ fontSize: 12.5, mt: 0.5 }} color="text.secondary">
                    {t("financial.rejectionReason")}: {exp.rejectionReason}
                  </Typography>
                )}
                {exp.transactionId && (
                  <Button size="small" variant="outlined" color="secondary" sx={{ mt: 1.25 }} onClick={() => setTxId(exp.transactionId)}>
                    {t("financial.viewTransaction")}
                  </Button>
                )}
              </Box>
            )}
          </>
        )}

        {!loading && !exp && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>{t("common.error")}</Typography>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
        <Button variant="outlined" color="secondary" onClick={onClose}>{t("common.close")}</Button>
        <Box sx={{ flex: 1 }} />
        {exp && exp.status === "pending_approval" && onApprove && onReject && (
          <>
            <Button variant="outlined" color="error" disabled={busy} onClick={() => onReject(expenseId)}>
              {t("financial.reject")}
            </Button>
            <Button variant="contained" color="secondary" disabled={busy} onClick={() => onApprove(expenseId)}>
              {t("financial.approve")}
            </Button>
          </>
        )}
      </DialogActions>

      <TransactionDetailDialog mode={mode} open={!!txId} txId={txId} onClose={() => setTxId(null)} />
      <SlipLightbox filename={lightbox} onClose={() => setLightbox(null)} t={t} getEvidenceUrl={getEvidenceUrl} />
    </Dialog>
  );
}
